import { h, defineComponent, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, apiErrorMessage, loadingButton, pageSubtitle } from '@/ui/components';
import { useTask, useValidation } from '@/ui/concerns';
import { updatePassword } from '@/services/api/auth';
import { PasswordChangingUser, validate as validatePassword } from '@/entities/auth/passwordChangingUser';
import { inputField } from '@/ui/components/form';
import { toasted } from '@/ui/helpers';
import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';

export const ChangePassword = defineComponent({
  name: 'ChangePassword',
  setup() {
    const password = ref<PasswordChangingUser>({
      password: '',
      confirm: '',
    });

    const {
      run: updatePasswordTask,
      state: updatePasswordState,
    } = useTask(
      () => updatePassword(password.value),
      () => {
        toasted.success(t('profile.password-updated'));
      },
    );

    const {
      validationErrors: passwordValidationErrors,
      hasNoValidationErrors: passwordHasNoValidationErrors,
      showValidationErrors: passwordShowValidationErrors,
    } = useValidation(validatePassword, password, updatePasswordState);

    const handlePasswordUpdate = () => {
      passwordShowValidationErrors.value = true;
      if (passwordHasNoValidationErrors.value) {
        trackClick(CLICK_EVENTS.CHANGE_PASSWORD);
        updatePasswordTask();
      }
    };

    return function render() {
      return box([h('form', [
        pageSubtitle(t('profile.change-password')),
        inputField({
          name: 'password',
          type: 'password',
          label: t('profile.new-password'),
          placeholder: t('auth.enter-new-password'),
          value: password.value.password,
          required: true,
          errors: passwordValidationErrors.value,
          disabled: updatePasswordState.value.pending,
        }, v => {
          password.value.password = v;
        }),
        inputField({
          name: 'confirm',
          type: 'password',
          label: t('profile.confirm-password'),
          placeholder: t('profile.confirm-password-again'),
          value: password.value.confirm,
          required: true,
          errors: passwordValidationErrors.value,
          disabled: updatePasswordState.value.pending,
        }, v => {
          password.value.confirm = v;
        }),

        apiErrorMessage(updatePasswordState.value),

        loadingButton(
          t('profile.change-password'),
          handlePasswordUpdate,
          updatePasswordState.value.pending,
        ),
      ])]);
    };
  },
});
